import React, { useState, useEffect } from 'react'
import type { ReactElement, ReactNode } from 'react'
import dynamic from 'next/dynamic'
import type { AppProps } from 'next/app'
import '@/style/tailwind.css'
import '@/style/global.css'
import '@/style/flip-clock.css'
import '@/styles/fireworks.css'
import '@/styles/timer-alert.css'
import { NextIntlClientProvider } from 'next-intl'
import 'antd/dist/reset.css'
import Head from '@/components/Header'
import ModelProvider from 'plugin-model/Provider'
import 'braft-editor/dist/index.css'
import 'braft-editor/dist/output.css'
import 'animate.css'
import '@/styles/anchor.css' // 必须最后引入:压过第三方样式给 <a> 加的默认下划线

import type { NextPage } from 'next'
import { StyleProvider } from '@ant-design/cssinjs'
import { SWRConfig } from 'swr'
import { ConfigProvider } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import BrowserVersionCheck from '@/components/BrowserVersionCheck'
import ErrorBoundary from '@/components/ErrorBounddary'
import { DarkThemeProvider } from '@/components/DarkTheme'
import ThemeLayout from '@/components/ThemeLayout'
import StaticNav from '@/components/Seo/StaticNav'
import SiteFooter from '@/components/Seo/SiteFooter'
import { SSR_ROUTES } from '@/utils/seo'
import { useRouter } from 'next/router'

declare let WeixinJSBridge: any
declare let document: any

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode
}
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}
const Layout = dynamic(() => import('@/components/Layout'), { ssr: false })

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const [locale, setLocale] = useState('zh')
  const getLayout = Component.getLayout ?? ((page) => page)
  const router = useRouter()
  // 公开内容页服务端直出正文(SEO/GEO):先渲染静态顶栏,客户端再换成带登录态的 Layout 顶栏,正文节点不动。
  const isSsrPage = SSR_ROUTES.has(router.pathname)
  const [ChromeLayout, setChromeLayout] = useState<React.ComponentType<any> | null>(null)
  useEffect(() => {
    if (isSsrPage && !ChromeLayout) import('@/components/Layout').then((m) => setChromeLayout(() => m.default))
  }, [isSsrPage, ChromeLayout])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentHost = window.location.hostname
      const currentPath = window.location.pathname + window.location.search + window.location.hash

      if (currentHost === 'gd.lingti.com') {
        window.location.replace(`https://hy.lingti.com${currentPath}`)
      }
    }
  }, [])

  useEffect(() => {
    const storedLocale = localStorage.getItem('locale') as 'zh' | 'lo'
    if (storedLocale) {
      setLocale(storedLocale)
    } else {
      setLocale('zh')
    }
  }, [])

  useEffect(() => {
    const packageJson = require('../package.json')
    const version = packageJson.version
    const currentVersion = localStorage.getItem('version')
    if (currentVersion) {
      if (version > currentVersion) {
        localStorage.setItem('version', version)
        localStorage.removeItem('timer-left')
        localStorage.removeItem('timer-top')
        localStorage.setItem('scaleLevel', '100')
        localStorage.removeItem('qrcode-left')
        localStorage.removeItem('qrcode-top')
        localStorage.setItem('qrcodeScaleLevel', '100')
      }
    } else {
      localStorage.setItem('version', version)
    }
  }, [])

  useEffect(() => {
    function handleFontSize() {
      // 设置网页字体为默认大小
      WeixinJSBridge.invoke('setFontSizeCallback', { fontSize: 0 })
      // 重写设置网页字体大小的事件
      WeixinJSBridge.on('menu:setfont', function () {
        WeixinJSBridge.invoke('setFontSizeCallback', { fontSize: 0 })
      })
    }
    if (typeof WeixinJSBridge === 'object' && typeof WeixinJSBridge.invoke === 'function') {
      handleFontSize()
    } else if (document.addEventListener) {
      document.addEventListener('WeixinJSBridgeReady', handleFontSize, false)
    } else if (document.attachEvent) {
      document.attachEvent('WeixinJSBridgeReady', handleFontSize)
      document.attachEvent('onWeixinJSBridgeReady', handleFontSize)
    }
  }, [])

  return (
    <NextIntlClientProvider locale={locale} messages={pageProps.messages}>
      <ModelProvider>
        <ErrorBoundary>
          <ConfigProvider locale={zhCN}>
            <DarkThemeProvider>
              <ThemeLayout>
                <SWRConfig value={{ revalidateOnFocus: false, refreshInterval: 900000, fallback: pageProps.fallback ?? {} }}>
                  <StyleProvider hashPriority="high">
                    <Head component={Component} seo={pageProps.seo} />
                    {isSsrPage ? (
                      <>
                        {ChromeLayout ? (
                          <ChromeLayout chromeOnly need_login={pageProps.need_login} hide_menu={pageProps.hide_menu} app_need_login={pageProps.app_need_login} />
                        ) : (
                          <StaticNav transparent={router.pathname === '/'} />
                        )}
                        <div className="h-full pt-16">
                          <BrowserVersionCheck>{getLayout(<Component {...pageProps} />)}</BrowserVersionCheck>
                          {router.pathname !== '/' && <SiteFooter />}
                        </div>
                      </>
                    ) : (
                      <Layout need_login={pageProps.need_login} hide_menu={pageProps.hide_menu} app_need_login={pageProps.app_need_login}>
                        <BrowserVersionCheck>{getLayout(<Component {...pageProps} />)}</BrowserVersionCheck>
                      </Layout>
                    )}
                  </StyleProvider>
                </SWRConfig>
              </ThemeLayout>
            </DarkThemeProvider>
          </ConfigProvider>
        </ErrorBoundary>
      </ModelProvider>
    </NextIntlClientProvider>
  )
}

export default MyApp
